/**
 * MATERIALES DE UN PARTE DE TRABAJO (FASE 9.5)
 *
 * Sección «Materiales» de la ficha del PT. Registrar un consumo NO mueve
 * stock: queda en borrador o pendiente de validación. Al VALIDAR (encargado,
 * ADMIN/CONSULTOR) se genera una salida de stock (operación CONSUMO_PT) que ya
 * no se edita; si el material vuelve, se registra una DEVOLUCIÓN.
 *
 * Móvil primero: tarjetas por consumo; el formulario sigue el orden almacén →
 * artículo → cantidad / lote / series → intervención, porque el almacén filtra
 * lo demás (artículos configurados, lotes con stock, series presentes).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  api,
  type AlmacenConsumo,
  type ConsumoPt,
  type EstadoConsumoPt,
  type MaterialesPt,
  type OpcionArticuloStock,
  type OpcionLote,
  type OpcionSerie,
} from '../api.ts';
import { Error as ErrorCaja, Estado as EstadoBadge, fecha, fechaHora, Modal, Panel, useDatos } from '../componentes/base.tsx';
import { ESTADOS_CONSUMO_PT, estadoConsumoPt } from '../estados.ts';
import { Buscador } from '../componentes/buscador.tsx';
import { buscarArticulos, erroresDe, ErrorStock, nombreArticulo } from './stock.tsx';
import { EditorSeries, SelectorLote, seriesUnicas } from './trazabilidad.tsx';
import { Escaner } from '../componentes/escaner.tsx';
import { anadirSerie, opcionArticulo, opcionLote, sumar } from '../escaner/opciones.ts';
import type { Lectura } from '../escaner/tipos.ts';
import { codigoAlmacen, codigoVisible } from '../componentes/codigo-articulo.tsx';

//  UX-1C: [etiqueta, tono] del mapa central (`estados.ts`).
export const ESTADOS_CONSUMO = Object.fromEntries(
  Object.entries(ESTADOS_CONSUMO_PT).map(([k, x]) => [k, [x.label, x.tone]]),
) as Readonly<Record<EstadoConsumoPt, [string, string]>>;
export const TEXTO_VALIDAR = 'Al validar se generará una salida de stock y no podrá editarse.';
export const TEXTO_REGISTRO = 'Registrar material no descuenta stock: se descuenta cuando quien gestiona el parte lo valida.';

type Filtro = 'TODOS' | 'PENDIENTE_VALIDACION' | 'BORRADOR' | 'VALIDADO' | 'CERRADOS';
interface IntervencionRef { id: string; etiqueta: string; finalizada: boolean }

const Estado = ({ e }: { e: EstadoConsumoPt }): ReactNode => <EstadoBadge d={estadoConsumoPt(e)} />;
const cant = (c: { cantidad: string; unidad: { codigo: string } }): string => `${c.cantidad} ${c.unidad.codigo}`;

// =================================================================== sección

export function MaterialesParte({ idPt, intervenciones, alCambiar }: {
  idPt: string; intervenciones: IntervencionRef[]; alCambiar?: () => void;
}): ReactNode {
  const base = `/api/mantenimiento/partes/${idPt}`;
  const datos = useDatos(() => api.get<MaterialesPt>(`${base}/materiales`), [idPt]);
  const [filtro, setFiltro] = useState<Filtro>('TODOS');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [dialogo, setDialogo] = useState<null | { t: 'nuevo' } | { t: 'editar'; c: ConsumoPt } | { t: 'validar' } | { t: 'rechazar'; c: ConsumoPt }
    | { t: 'anular'; c: ConsumoPt } | { t: 'devolver'; c: ConsumoPt } | { t: 'rechazar-dev'; idDev: string }>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function accion(f: () => Promise<unknown>, ok?: (r: unknown) => void): Promise<boolean> {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      const r = await f();
      setDialogo(null);
      setSel(new Set());
      ok?.(r);
      datos.recargar();
      alCambiar?.();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setOcupado(false);
    }
  }

  const d = datos.datos;
  const visibles = (d?.consumos ?? []).filter((c) => filtro === 'TODOS' ? true
    : filtro === 'CERRADOS' ? c.estado === 'RECHAZADO' || c.estado === 'ANULADO' : c.estado === filtro);
  const pendientes = (d?.consumos ?? []).filter((c) => c.estado === 'PENDIENTE_VALIDACION');
  const seleccionados = pendientes.filter((c) => sel.has(c.id));
  const alternar = (id: string): void => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <section className="panel" aria-label="Materiales" id="materiales">
      <div className="panel-cabecera fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h2>Materiales</h2>
        {d?.puede.registrar === true && <button className="principal" onClick={() => setDialogo({ t: 'nuevo' })}>Añadir material</button>}
      </div>
      <div className="panel-cuerpo">
        <Panel estado={datos}>
          {(m) => (
            <>
              <div className="cifras resumen-materiales" aria-label="Resumen de materiales">
                <span className="cifra"><strong>{m.resumen.pendientes + m.resumen.borradores}</strong> consumos pendientes</span>
                <span className="cifra"><strong>{m.resumen.validados}</strong> validados</span>
                <span className="cifra"><strong>{m.resumen.rechazados}</strong> rechazados</span>
                <span className="cifra"><strong>{m.resumen.devolucionesValidadas}</strong> devoluciones validadas</span>
                {/* FASE 9.5-B (UAT): la devolución pendiente se ve como pendiente, no escondida entre paréntesis. */}
                {m.resumen.devolucionesPendientes > 0 && <span className="cifra aviso-texto"><strong>{m.resumen.devolucionesPendientes}</strong> {m.resumen.devolucionesPendientes === 1 ? 'devolución pendiente' : 'devoluciones pendientes'} de validación</span>}
              </div>
              {m.consumos.length === 0 ? (
                <p className="suave">Sin materiales registrados.{m.puede.registrar ? ` ${TEXTO_REGISTRO}` : ''}</p>
              ) : (
                <>
                  <div className="fila filtros-materiales" style={{ flexWrap: 'wrap' }}>
                    <label className="campo"><span>Mostrar</span>
                      <select value={filtro} onChange={(ev) => setFiltro(ev.target.value as Filtro)}>
                        <option value="TODOS">Todos</option><option value="PENDIENTE_VALIDACION">Pendientes de validar</option>
                        <option value="BORRADOR">Borradores</option><option value="VALIDADO">Validados</option><option value="CERRADOS">Rechazados y anulados</option>
                      </select>
                    </label>
                    {m.puede.validar && pendientes.length > 0 && (
                      <div className="barra-validar fila" role="group" aria-label="Validación de consumos">
                        <button type="button" onClick={() => setSel(new Set(pendientes.map((c) => c.id)))}>Seleccionar pendientes ({pendientes.length})</button>
                        <button type="button" className="principal" disabled={seleccionados.length === 0} onClick={() => setDialogo({ t: 'validar' })}>
                          Validar seleccionados ({seleccionados.length})
                        </button>
                      </div>
                    )}
                  </div>
                  {aviso !== null && <p className="nota exito" role="status">{aviso}</p>}
                  {error !== null && dialogo === null && <ErrorStock error={error} />}
                  <ul className="lista-materiales" aria-label="Consumos de material">
                    {visibles.map((c) => (
                      <TarjetaConsumo key={c.id} c={c} m={m} seleccionado={sel.has(c.id)} alternar={() => alternar(c.id)} ocupado={ocupado}
                        enviar={() => void accion(() => api.post(`${base}/consumos/${c.id}/enviar`, { versionEsperada: c.version }))}
                        editar={() => setDialogo({ t: 'editar', c })} anular={() => setDialogo({ t: 'anular', c })}
                        rechazar={() => setDialogo({ t: 'rechazar', c })} devolver={() => setDialogo({ t: 'devolver', c })}
                        validarDev={(idDev) => void accion(() => api.post(`${base}/devoluciones/${idDev}/validar`, {}))}
                        rechazarDev={(idDev) => setDialogo({ t: 'rechazar-dev', idDev })}
                        anularDev={(idDev) => void accion(() => api.post(`${base}/devoluciones/${idDev}/anular`, {}))} />
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Panel>
      </div>

      {d !== null && dialogo?.t === 'nuevo' && (
        <FormConsumo idPt={idPt} intervenciones={intervenciones} puedeValidar={d.puede.validar} error={error} cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => accion(() => api.post(`${base}/consumos`, cuerpo))} />
      )}
      {d !== null && dialogo?.t === 'editar' && (
        <FormConsumo idPt={idPt} intervenciones={intervenciones} puedeValidar={false} consumo={dialogo.c} error={error} cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => accion(() => api.patch(`${base}/consumos/${dialogo.c.id}`, { ...cuerpo, versionEsperada: dialogo.c.version }))} />
      )}
      {d !== null && dialogo?.t === 'validar' && (
        <Modal titulo="Validar consumos" cerrar={() => setDialogo(null)}
          pie={<><button onClick={() => setDialogo(null)}>Cancelar</button>
            <button className="principal" disabled={ocupado} onClick={() => void accion(() => api.post<MaterialesPt>(`${base}/consumos/validar`, { ids: seleccionados.map((c) => c.id) }),
              (r) => setAviso(`Validado. Salida de stock: ${((r as MaterialesPt).operaciones ?? []).map((o) => o.codigo).join(', ')}.`))}>
              Validar {seleccionados.length} consumo(s)</button></>}>
          {error !== null && <ErrorStock error={error} />}
          <p><strong>{d.parte.codigo}</strong> · {seleccionados.length} consumo(s)</p>
          <div className="tabla-desliza">
            <table aria-label="Consumos a validar" className="tabla-tarjetas">
              <thead><tr><th>Artículo</th><th>Almacén</th><th className="num">Cantidad</th><th>Lote / series</th><th className="num">Disponible</th></tr></thead>
              <tbody>
                {seleccionados.map((c) => (
                  <tr key={c.id}>
                    <td className="celda-titulo">{nombreArticulo(c.articulo)}</td>
                    <td data-label="Almacén">{codigoAlmacen(c.almacen)}</td>
                    <td className="num" data-label="Cantidad">{cant(c)}</td>
                    <td data-label="Lote / series" className="pequena">{[c.lote?.codigo, c.series.map((s) => s.numero).join(', ')].filter(Boolean).join(' · ') || '—'}
                      {c.lote?.caducado === true && <span className="etiqueta error">Caducado</span>}</td>
                    <td className={`num${c.disponible !== null && Number(c.disponible) < Number(c.cantidadStock) ? ' saldo-negativo' : ''}`} data-label="Disponible">
                      {c.disponible ?? '—'} {c.articulo.unidadStock.codigo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="nota aviso" role="note">{TEXTO_VALIDAR}</p>
          <p className="suave pequena">El disponible es informativo: el stock se comprueba al validar. Si un consumo no puede salir, no se valida ninguno.</p>
        </Modal>
      )}
      {d !== null && (dialogo?.t === 'rechazar' || dialogo?.t === 'anular' || dialogo?.t === 'rechazar-dev') && (
        <DialogoMotivo
          titulo={dialogo.t === 'rechazar' ? 'Rechazar consumo' : dialogo.t === 'anular' ? 'Anular consumo' : 'Rechazar devolución'}
          explicacion={dialogo.t === 'rechazar' ? 'No mueve stock. Quien lo registró verá el motivo y podrá registrar uno corregido.'
            : dialogo.t === 'anular' ? (dialogo.c.estado === 'BORRADOR' ? 'El borrador queda anulado. No mueve stock.' : 'Anular un consumo pendiente exige motivo. No mueve stock.')
            : 'La devolución no se aplica: el stock no cambia.'}
          obligatorio={dialogo.t !== 'anular' || dialogo.c.estado !== 'BORRADOR'}
          error={error} cerrar={() => setDialogo(null)}
          enviar={(motivo) => void accion(() => {
            if (dialogo.t === 'rechazar') return api.post(`${base}/consumos/${dialogo.c.id}/rechazar`, { motivo, versionEsperada: dialogo.c.version });
            if (dialogo.t === 'anular') return api.post(`${base}/consumos/${dialogo.c.id}/anular`, { motivo, versionEsperada: dialogo.c.version });
            return api.post(`${base}/devoluciones/${dialogo.idDev}/rechazar`, { motivo });
          })} />
      )}
      {d !== null && dialogo?.t === 'devolver' && (
        <FormDevolucion c={dialogo.c} puedeValidar={d.puede.validar} ptCerrado={d.parte.estado === 'CERRADO'} error={error} cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => void accion(() => api.post(`${base}/consumos/${dialogo.c.id}/devoluciones`, cuerpo))} />
      )}
    </section>
  );
}

// ================================================================== tarjeta

function TarjetaConsumo({ c, m, seleccionado, alternar, ocupado, enviar, editar, anular, rechazar, devolver, validarDev, rechazarDev, anularDev }: {
  c: ConsumoPt; m: MaterialesPt; seleccionado: boolean; alternar: () => void; ocupado: boolean;
  enviar: () => void; editar: () => void; anular: () => void; rechazar: () => void; devolver: () => void;
  validarDev: (id: string) => void; rechazarDev: (id: string) => void; anularDev: (id: string) => void;
}): ReactNode {
  const pendiente = c.estado === 'PENDIENTE_VALIDACION';
  return (
    <li className={`tarjeta-material estado-${c.estado.toLowerCase()}`} aria-label={`${codigoVisible(c.articulo)} · ${c.articulo.descripcion}`}>
      <div className="fila tarjeta-material-cabecera" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div className="fila" style={{ gap: 8, alignItems: 'flex-start' }}>
          {pendiente && m.puede.validar && (
            <input type="checkbox" checked={seleccionado} onChange={alternar} aria-label={`Seleccionar ${c.articulo.codigo} para validar`} />
          )}
          <div><strong>{c.articulo.descripcion}</strong> <Link className="mono pequena" to={`/inventario/articulos/${c.articulo.id}`}>{c.articulo.codigo}</Link>
            <div className="cantidad-material">{cant(c)}{c.unidad.id !== c.articulo.unidadStock.id && <span className="suave pequena"> (= {c.cantidadStock} {c.articulo.unidadStock.codigo})</span>}</div>
          </div>
        </div>
        <Estado e={c.estado} />
      </div>
      <div className="pequena datos-material">
        <span>Almacén {codigoAlmacen(c.almacen)}</span>
        {c.lote !== null && <span>Lote <Link className="mono" to={`/inventario/lotes/${c.lote.id}`}>{c.lote.codigo}</Link>{c.lote.fechaCaducidad !== null && ` · cad. ${fecha(c.lote.fechaCaducidad)}`}
          {c.lote.caducado && <span className="etiqueta error">Lote caducado</span>}</span>}
        {c.series.length > 0 && <span>Series {c.series.map((s, k) => <span key={s.id}>{k > 0 && ', '}<Link className="mono" to={`/inventario/series/${s.id}`}>{s.numero}</Link></span>)}</span>}
        {c.intervencion !== null && <span>Intervención de {c.intervencion.operario} · {fechaHora(c.intervencion.inicioEn)}</span>}
        {c.operacion !== null && <span>Salida <Link className="mono" to={`/inventario/operaciones/${c.operacion.id}`}>{c.operacion.codigo}</Link></span>}
      </div>
      {c.observaciones !== null && <p className="pequena suave">{c.observaciones}</p>}
      {c.estado === 'RECHAZADO' && <p className="nota error pequena">Rechazado por {c.rechazadoPor?.nombre}: {c.motivoRechazo}</p>}
      {c.estado === 'ANULADO' && c.motivoAnulacion !== null && <p className="pequena suave">Anulado: {c.motivoAnulacion}</p>}
      <div className="suave pequena">Registrado por {c.registradoPor.nombre} · {fechaHora(c.registradoEn)}{c.validadoPor !== null && ` · validado por ${c.validadoPor.nombre}`}</div>
      {c.devoluciones.length > 0 && (
        <ul className="lista-devoluciones" aria-label={`Devoluciones de ${c.articulo.codigo}`}>
          {c.devoluciones.map((dv) => (
            <li key={dv.id} className="pequena">
              Devolución {dv.cantidad} {dv.unidad.codigo}{dv.series.length > 0 && ` (${dv.series.map((s) => s.numero).join(', ')})`} ·{' '}
              <Estado e={dv.estado} />
              {dv.operacion !== null && <> · <Link className="mono" to={`/inventario/operaciones/${dv.operacion.id}`}>{dv.operacion.codigo}</Link></>}
              {dv.motivoRechazo !== null && <span className="suave"> · {dv.motivoRechazo}</span>}
              {dv.estado === 'PENDIENTE_VALIDACION' && (
                <span className="acciones-devolucion">
                  {m.puede.validar && <button type="button" className="pequeno" disabled={ocupado} onClick={() => validarDev(dv.id)}>Validar devolución</button>}
                  {m.puede.rechazar && <button type="button" className="pequeno" onClick={() => rechazarDev(dv.id)}>Rechazar</button>}
                  <button type="button" className="pequeno" onClick={() => anularDev(dv.id)}>Anular</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="fila acciones-material" style={{ flexWrap: 'wrap' }}>
        {c.estado === 'BORRADOR' && m.puede.registrar && <>
          <button type="button" onClick={editar}>Editar</button>
          <button type="button" className="principal" disabled={ocupado} onClick={enviar}>Enviar a validación</button>
          <button type="button" onClick={anular}>Anular</button>
        </>}
        {pendiente && m.puede.rechazar && <button type="button" onClick={rechazar}>Rechazar</button>}
        {pendiente && m.puede.validar && <button type="button" onClick={anular}>Anular</button>}
        {c.estado === 'VALIDADO' && m.puede.devolver && Number(c.maximoDevolvible) > 0 && <button type="button" onClick={devolver}>Devolver material</button>}
      </div>
    </li>
  );
}

// ============================================================ formularios

function DialogoMotivo({ titulo, explicacion, obligatorio, error, cerrar, enviar }: {
  titulo: string; explicacion: string; obligatorio: boolean; error: unknown; cerrar: () => void; enviar: (motivo: string) => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const campos = erroresDe(error);
  return (
    <Modal titulo={titulo} cerrar={cerrar}
      pie={<><button onClick={cerrar}>Volver</button><button className="principal" disabled={obligatorio && motivo.trim().length < 5} onClick={() => enviar(motivo.trim())}>{titulo}</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <p>{explicacion}</p>
      <div className="campo"><label htmlFor="mot-mat">Motivo{obligatorio ? ' (obligatorio)' : ' (opcional)'}</label>
        <textarea id="mot-mat" rows={2} maxLength={2000} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        {campos['motivo'] !== undefined && <div className="error-campo">{campos['motivo']}</div>}</div>
    </Modal>
  );
}

const buscarAlmacenesConsumo = (idPt: string) => async (texto: string): Promise<AlmacenConsumo[]> =>
  (await api.get<{ filas: AlmacenConsumo[] }>(`/api/mantenimiento/partes/${idPt}/materiales/almacenes?${new URLSearchParams({ texto }).toString()}`)).filas;
const buscarSeriesEn = (idArticulo: string, idAlmacen: string, idLote: string | null) => async (texto: string) => {
  const p = new URLSearchParams({ idArticulo, idAlmacen, enStock: 'true', texto, limite: '20' });
  if (idLote !== null) p.set('idLote', idLote);
  return (await api.get<{ filas: OpcionSerie[] }>(`/api/inventario/opciones/series?${p.toString()}`)).filas;
};

/** Alta (y edición de borrador) de un consumo. Orden: almacén → artículo → cantidad/lote/series → intervención. */
function FormConsumo({ idPt, intervenciones, puedeValidar, consumo, error, cerrar, enviar }: {
  idPt: string; intervenciones: IntervencionRef[]; puedeValidar: boolean; consumo?: ConsumoPt; error: unknown;
  cerrar: () => void; enviar: (cuerpo: Record<string, unknown>) => Promise<boolean>;
}): ReactNode {
  const editando = consumo !== undefined;
  const [almacen, setAlmacen] = useState<AlmacenConsumo | null>(consumo === undefined ? null : { ...consumo.almacen, tipo: '', centro: null });
  //  F13.4: el técnico consume, por defecto, de SU furgoneta (si la tiene y vale para este parte). Puede cambiarlo.
  useEffect(() => {
    if (consumo !== undefined) return;
    void buscarAlmacenesConsumo(idPt)('').then((xs) => {
      const propio = xs.find((x) => x.propio === true);
      if (propio !== undefined) setAlmacen((a) => a ?? propio);
    }).catch(() => undefined);
  }, [idPt]);
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(consumo === undefined ? null : {
    id: consumo.articulo.id, codigo: consumo.articulo.codigo, descripcion: consumo.articulo.descripcion, unidadStock: consumo.articulo.unidadStock,
    unidades: [{ ...consumo.articulo.unidadStock, factor: '1' }, ...(consumo.unidad.id !== consumo.articulo.unidadStock.id ? [{ ...consumo.unidad, factor: consumo.factorConversion }] : [])],
    saldo: consumo.disponible, trazabilidad: { controlaLotes: consumo.articulo.controlaLotes, controlaSeries: consumo.articulo.controlaSeries, controlaCaducidad: consumo.articulo.controlaCaducidad },
  });
  const [idUnidad, setIdUnidad] = useState(consumo?.unidad.id ?? '');
  const [cantidad, setCantidad] = useState(consumo?.cantidad ?? '');
  const [lote, setLote] = useState<OpcionLote | null>(consumo?.lote == null ? null
    : { id: consumo.lote.id, codigo: consumo.lote.codigo, fechaCaducidad: consumo.lote.fechaCaducidad, caducidad: { estado: consumo.lote.caducado ? 'CADUCADO' : 'VIGENTE', dias: null }, saldo: consumo.disponible, activo: true });
  const [series, setSeries] = useState((consumo?.series ?? []).map((s) => s.numero).join('\n'));
  const [idIntervencion, setIdIntervencion] = useState(consumo?.intervencion?.id ?? '');
  const [observaciones, setObservaciones] = useState(consumo?.observaciones ?? '');
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const traz = articulo?.trazabilidad;
  const serializado = traz?.controlaSeries === true;
  const conLotes = traz?.controlaLotes === true;
  const interv = intervenciones.find((i) => i.id === idIntervencion);
  const listo = almacen !== null && articulo !== null && (!conLotes || lote !== null) && (serializado ? seriesUnicas(series).length > 0 : cantidad.trim() !== '');

  /** F13.3: escanear rellena artículo / lote / serie / cantidad. Es un borrador: nada se mueve hasta validar. */
  async function leerMaterial(l: Lectura): Promise<string | null> {
    if (almacen === null) return 'Elige antes el almacén.';
    const r = l.recurso;
    if (r.articulo === null) return 'La lectura no identifica un artículo.';
    const art = r.articulo.id === articulo?.id ? articulo : await opcionArticulo(r.articulo.id, r.articulo.codigo, almacen.id);
    if (art === null) return `${r.articulo.codigoNegocio} no está configurado en el almacén ${codigoAlmacen(almacen)}.`;
    let lo = r.articulo.id === articulo?.id ? lote : null;
    if (r.lote !== null) {
      if (r.lote.estado === 'BLOQUEADO') return `El lote ${r.lote.codigo} está bloqueado: no puede consumirse.`;
      lo = await opcionLote(art.id, r.lote.id, r.lote.codigo, almacen.id);
      if (lo === null) return `El lote ${r.lote.codigo} no está disponible en este almacén.`;
      if (Number(lo.saldo ?? '0') <= 0) return `El lote ${r.lote.codigo} no tiene stock en ${codigoAlmacen(almacen)}.`;
    }
    const mismo = art.id === articulo?.id && (lo?.id ?? null) === (lote?.id ?? null);
    if (art.trazabilidad?.controlaSeries === true) {
      const numero = r.serie?.numero ?? null;
      const base = mismo ? series : '';
      if (numero !== null) {
        const n = anadirSerie(base, numero);
        if (n === null) return `La serie ${numero} ya está en este consumo.`;
        setSeries(n);
      } else if (!mismo) setSeries('');
    } else {
      setCantidad(mismo ? sumar(cantidad, l.cantidad ?? 1) : String(l.cantidad ?? 1));
      if (!mismo) setIdUnidad(art.unidadStock.id);
    }
    setArticulo(art);
    setLote(lo);
    return null;
  }

  async function guardar(accion: 'BORRADOR' | 'ENVIAR' | 'VALIDAR'): Promise<void> {
    setEnviando(true);
    const cuerpo: Record<string, unknown> = {
      ...(editando ? {} : { idAlmacen: almacen?.id ?? null, idArticulo: articulo?.id ?? null, accion }),
      ...(serializado ? { series: seriesUnicas(series) } : { cantidad, idUnidad: idUnidad || articulo?.unidadStock.id }),
      ...(conLotes ? { idLote: lote?.id ?? null } : {}),
      idIntervencion: idIntervencion || null, observaciones,
    };
    //  El error de la petición lo recoge la sección (accion) y vuelve aquí por `error` (general y de campo).
    try { await enviar(cuerpo); } finally { setEnviando(false); }
  }
  return (
    <Modal titulo={editando ? 'Editar material (borrador)' : 'Añadir material'} cerrar={cerrar}
      pie={<>
        <button type="button" onClick={cerrar}>Cancelar</button>
        {editando ? <button type="button" className="principal" disabled={!listo || enviando} onClick={() => void guardar('BORRADOR')}>Guardar cambios</button> : <>
          <button type="button" disabled={!listo || enviando} onClick={() => void guardar('BORRADOR')}>Guardar borrador</button>
          <button type="button" className="principal" disabled={!listo || enviando} onClick={() => void guardar('ENVIAR')}>Enviar a validación</button>
          {puedeValidar && <button type="button" className="principal" disabled={!listo || enviando} onClick={() => void guardar('VALIDAR')}>Guardar y validar</button>}
        </>}
      </>}>
      <div onKeyDown={(ev) => { if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') ev.preventDefault(); }}>
        <p className="nota info pequena" role="note">{TEXTO_REGISTRO}</p>
        {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
        <div className="form-material">
          {editando ? (
            <p className="pequena">Almacén <strong>{almacen == null ? '' : codigoAlmacen(almacen)}</strong> · {nombreArticulo(consumo.articulo)}. Para cambiar de almacén o de artículo, anula este borrador y registra otro.</p>
          ) : (
            <>
              <Buscador<AlmacenConsumo> etiqueta="1. Almacén" valor={almacen} alElegir={(a) => { setAlmacen(a); setArticulo(null); setLote(null); setSeries(''); }}
                buscar={buscarAlmacenesConsumo(idPt)} clave={(a) => a.id} texto={(a) => `${codigoAlmacen(a)} · ${a.descripcion}`}
                detalle={(a) => (a.centro === null ? 'Sin centro' : a.centro)} error={campos['idAlmacen']}
                sinResultados="No hay almacenes de este centro disponibles para ti." />
              <Buscador<OpcionArticuloStock> etiqueta="2. Artículo" valor={articulo} contexto={almacen?.id ?? ''} deshabilitado={almacen === null}
                placeholder={almacen === null ? 'Elige antes el almacén' : 'Código o descripción…'}
                buscar={buscarArticulos(almacen?.id ?? null)} clave={(a) => a.id} texto={nombreArticulo}
                detalle={(a) => `Disponible ${a.saldo ?? '0'} ${a.unidadStock.codigo}`}
                alElegir={(a) => { setArticulo(a); setIdUnidad(a?.unidadStock.id ?? ''); setLote(null); setSeries(''); }}
                error={campos['idArticulo']} sinResultados="Ningún artículo activo configurado en este almacén." />
            </>
          )}
          {!editando && (
            <Escaner titulo="Escanear material" contexto={{ uso: 'CONSUMO_PT', idAlmacen: almacen?.id ?? null }}
              deshabilitado={almacen === null ? 'Elige antes el almacén para escanear.' : null} alLeer={leerMaterial} />
          )}
          {articulo !== null && almacen !== null && conLotes && (
            <SelectorLote articulo={{ ...articulo, controlaCaducidad: traz?.controlaCaducidad }} idAlmacen={almacen.id} conStock
              etiqueta="3. Lote (sólo con stock aquí)" valor={lote} alElegir={(l) => { setLote(l); setSeries(''); }} error={campos['idLote']} />
          )}
          {articulo !== null && !serializado && (
            <div className="form-rejilla">
              <div className="campo"><label htmlFor="mat-cant">{conLotes ? '4.' : '3.'} Cantidad</label>
                <input id="mat-cant" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
                {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
              <div className="campo"><label htmlFor="mat-ud">Unidad</label>
                <select id="mat-ud" value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
                  {articulo.unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${articulo.unidadStock.codigo})`}</option>)}
                </select>
                {campos['idUnidad'] !== undefined && <div className="error-campo">{campos['idUnidad']}</div>}</div>
            </div>
          )}
          {articulo !== null && almacen !== null && serializado && (!conLotes || lote !== null) && (
            <EditorSeries id="mat-series" etiqueta="Números de serie que se usan (1 serie = 1 unidad)" valor={series} cambiar={setSeries}
              error={campos['series'] ?? campos['cantidad']} disponibles={buscarSeriesEn(articulo.id, almacen.id, lote?.id ?? null)} />
          )}
          {articulo !== null && (
            <p className="suave pequena" aria-live="polite">
              {lote !== null ? <>Disponible del lote {lote.codigo} aquí: <strong>{lote.saldo ?? '0'} {articulo.unidadStock.codigo}</strong></>
                : <>Disponible aquí: <strong>{articulo.saldo ?? '0'} {articulo.unidadStock.codigo}</strong></>} (informativo: se comprueba al validar)
              {lote?.caducidad.estado === 'CADUCADO' && <span className="etiqueta error">Lote caducado</span>}
            </p>
          )}
          {intervenciones.length > 0 && (
            <div className="campo"><label htmlFor="mat-int">Intervención (opcional)</label>
              <select id="mat-int" value={idIntervencion} onChange={(ev) => setIdIntervencion(ev.target.value)}>
                <option value="">Material del parte (sin intervención concreta)</option>
                {intervenciones.map((i) => <option key={i.id} value={i.id}>{i.etiqueta}{i.finalizada ? ' — finalizada' : ''}</option>)}
              </select>
              {interv?.finalizada === true && <p className="suave pequena">Intervención finalizada: sólo quien gestiona puede imputarle material, explicando el motivo en observaciones.</p>}
              {campos['idIntervencion'] !== undefined && <div className="error-campo">{campos['idIntervencion']}</div>}</div>
          )}
          <div className="campo"><label htmlFor="mat-obs">Observaciones</label>
            <textarea id="mat-obs" rows={2} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} />
            {campos['observaciones'] !== undefined && <div className="error-campo">{campos['observaciones']}</div>}</div>
        </div>
      </div>
    </Modal>
  );
}

/** Devolver material de un consumo validado: total o parcial; si es serializado, sólo sus series no devueltas. */
function FormDevolucion({ c, puedeValidar, ptCerrado = false, error, cerrar, enviar }: {
  c: ConsumoPt; puedeValidar: boolean; ptCerrado?: boolean; error: unknown; cerrar: () => void; enviar: (cuerpo: Record<string, unknown>) => void;
}): ReactNode {
  const yaDevueltas = new Set(c.devoluciones.filter((d) => d.estado === 'PENDIENTE_VALIDACION' || d.estado === 'VALIDADO').flatMap((d) => d.series.map((s) => s.id)));
  const devolvibles = c.series.filter((s) => !yaDevueltas.has(s.id));
  const [cantidad, setCantidad] = useState('');
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [observaciones, setObservaciones] = useState('');
  //  FASE 9.5-B: en un parte CERRADO la devolución se valida al registrarla (un PT cerrado no queda con nada pendiente).
  const [validarYa, setValidarYa] = useState(ptCerrado);
  const campos = erroresDe(error);
  const ud = c.articulo.unidadStock.codigo;
  const listo = c.articulo.controlaSeries ? marcadas.size > 0 : cantidad.trim() !== '' && Number(cantidad.replace(',', '.')) > 0;
  return (
    <Modal titulo={`Devolver material · ${c.articulo.codigo}`} cerrar={cerrar}
      pie={<><button onClick={cerrar}>Cancelar</button>
        <button className="principal" disabled={!listo} onClick={() => enviar({
          ...(c.articulo.controlaSeries ? { series: [...marcadas] } : { cantidad: cantidad.replace(',', '.'), idUnidad: c.articulo.unidadStock.id }),
          observaciones, ...(validarYa ? { validar: true } : {}),
        })}>Registrar devolución</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
      <p className="pequena">Material que vuelve físicamente al almacén {codigoAlmacen(c.almacen)}{c.lote !== null && <>, lote <span className="mono">{c.lote.codigo}</span></>}. Registrarlo no mueve stock: entra al validarlo.</p>
      <div className="cifras resumen-devolucion">
        <span className="cifra"><strong>{c.cantidadStock} {ud}</strong> consumido</span>
        <span className="cifra"><strong>{c.devuelto} {ud}</strong> ya devuelto</span>
        {Number(c.devolucionPendiente) > 0 && <span className="cifra"><strong>{c.devolucionPendiente} {ud}</strong> pendiente</span>}
        <span className="cifra"><strong>{c.maximoDevolvible} {ud}</strong> máximo devolvible</span>
      </div>
      {c.articulo.controlaSeries ? (
        <fieldset className="campo series-devolucion">
          <legend>Series que vuelven</legend>
          {devolvibles.length === 0 ? <p className="suave">No quedan series por devolver.</p> : devolvibles.map((s) => (
            <label key={s.id} className="casilla"><input type="checkbox" checked={marcadas.has(s.id)}
              onChange={() => setMarcadas((m) => { const n = new Set(m); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; })} /> <span className="mono">{s.numero}</span></label>
          ))}
          {campos['series'] !== undefined && <div className="error-campo">{campos['series']}</div>}
        </fieldset>
      ) : (
        <div className="campo"><label htmlFor="dev-cant">Cantidad a devolver ({ud})</label>
          <input id="dev-cant" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
          {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
      )}
      <div className="campo"><label htmlFor="dev-obs">Observaciones</label>
        <textarea id="dev-obs" rows={2} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} /></div>
      {ptCerrado
        ? <p className="nota aviso">El parte está cerrado: la devolución se valida al registrarla y entra en stock ahora.</p>
        : puedeValidar && <label className="casilla"><input type="checkbox" checked={validarYa} onChange={(ev) => setValidarYa(ev.target.checked)} /> Validar ahora (entra en stock al registrarla)</label>}
    </Modal>
  );
}
